// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.
//
// Thin wrappers around PostHog so the rest of the app never imports
// posthog-js directly. Tree-shakes cleanly when the key is absent.

import posthog from "posthog-js";

const KEY  = import.meta.env.VITE_POSTHOG_KEY  as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://us.i.posthog.com";

let initialised = false;

export function initAnalytics() {
  if (!KEY || initialised) return;
  posthog.init(KEY, {
    api_host:              HOST,
    capture_pageview:      false,   // we'll call capturePageview manually
    capture_pageleave:     true,
    autocapture:           false,   // explicit events only — avoids PII in element text
    session_recording:     { maskAllInputs: true },
    persistence:           "localStorage",
    loaded(ph) {
      if (import.meta.env.DEV) ph.debug();
    },
  });
  initialised = true;
}

// ── Identity ──────────────────────────────────────────────────────────────────
export function identifyUser(userId: string, props?: Record<string, unknown>) {
  if (!initialised) return;
  posthog.identify(userId, props);
}

export function resetIdentity() {
  if (!initialised) return;
  posthog.reset();
}

// ── Event tracking ───────────────────────────────────────────────────────────
export function track(event: string, props?: Record<string, unknown>) {
  if (!initialised) return;
  posthog.capture(event, props);
}

// ── Page views ───────────────────────────────────────────────────────────────
export function capturePageview(path: string) {
  if (!initialised) return;
  posthog.capture("$pageview", { $current_url: path });
}

// ── Named events used throughout the app ─────────────────────────────────────
export const Events = {
  // Auth
  SIGNUP:            "user_signed_up",
  LOGIN:             "user_logged_in",
  LOGOUT:            "user_logged_out",
  // Projects
  PROJECT_CREATE:    "project_created",
  PROJECT_OPEN:      "project_opened",
  PROJECT_DELETE:    "project_deleted",
  SANDBOX_OPEN:      "sandbox_opened",
  // Estimates
  ITEM_ADD:          "estimate_item_added",
  AI_IMPORT:         "ai_import_started",
  SNAPSHOT_CREATE:   "snapshot_created",
  // Exports
  EXPORT_CSV:        "export_csv",
  EXPORT_PDF:        "export_pdf",
  EXPORT_EXCEL:      "export_excel",
  EXPORT_PROCORE:    "export_procore",
  // Sharing / team
  INVITE_SENT:       "invite_sent",
  SHARE_DIALOG_OPEN: "share_dialog_opened",
  // Navigation
  PRICING_VIEW:      "pricing_page_viewed",
  SETTINGS_OPEN:     "settings_opened",
} as const;
